import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI, storesAPI } from '@/lib/api';
import { formatNumber, formatDate, theme } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Store,
  Users,
  Settings,
  LogOut,
  Sun,
  Moon,
  Plus,
  Shield,
  TrendingUp,
  Gift,
  Eye,
  Power,
  Loader2,
  Crown
} from 'lucide-react';

// Admin Dashboard
function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(theme.get() === 'dark');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newStoreData, setNewStoreData] = useState({
    name: '',
    description: '',
    owner_email: '',
    membership_plan: 'basic',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, storesRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.listStores(),
      ]);
      setStats(statsRes.data);
      setStores(storesRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme.toggle();
    setIsDark(newTheme === 'dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleToggleStore = async (store) => {
    try {
      await adminAPI.toggleStoreStatus(store.id, !store.is_active);
      toast.success(store.is_active ? 'Tienda desactivada' : 'Tienda activada');
      loadData();
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleCreateStore = async () => {
    if (!newStoreData.name || !newStoreData.owner_email) {
      toast.error('Nombre y email del owner son requeridos');
      return;
    }

    setCreating(true);
    try {
      await storesAPI.create(newStoreData);
      toast.success('Tienda creada exitosamente');
      setCreateDialogOpen(false);
      setNewStoreData({ name: '', description: '', owner_email: '', membership_plan: 'basic' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear tienda');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass px-8 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-outfit text-xl font-bold text-foreground">Platform Admin</h1>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-muted-foreground">
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="text-muted-foreground">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="p-8 max-w-7xl mx-auto">
        {/* Platform Stats */}
        <div className="mb-8">
          <h2 className="font-outfit text-2xl font-bold mb-6 text-foreground">Métricas de Plataforma</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Tiendas Totales</p>
                    <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_stores || 0)}</p>
                  </div>
                  <Store className="w-8 h-8 text-indigo-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Tiendas Activas</p>
                    <p className="font-outfit text-3xl font-bold text-green-400">{formatNumber(stats?.active_stores || 0)}</p>
                  </div>
                  <Power className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Clientes</p>
                    <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_customers || 0)}</p>
                  </div>
                  <Users className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Visitas Totales</p>
                    <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_visits || 0)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Canjes</p>
                    <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_redemptions || 0)}</p>
                  </div>
                  <Gift className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Stores Management */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-outfit text-2xl font-bold text-foreground">Gestión de Tiendas</h2>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-500" data-testid="create-store-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tienda
            </Button>
          </div>

          <div className="space-y-4">
            {stores.length === 0 ? (
              <Card className="glass border-white/10">
                <CardContent className="p-8 text-center">
                  <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No hay tiendas registradas</p>
                  <Button onClick={() => setCreateDialogOpen(true)} className="mt-4 bg-indigo-600 hover:bg-indigo-500">
                    Crear primera tienda
                  </Button>
                </CardContent>
              </Card>
            ) : (
              stores.map((store) => (
                <Card key={store.id} className="glass border-white/10" data-testid={`admin-store-${store.id}`}>
                  <CardContent className="p-6 flex items-center gap-6">
                    <div className="w-16 h-16 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                      <Store className="w-8 h-8 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-outfit text-lg font-bold text-foreground">{store.name}</h3>
                        <Badge className={store.is_active ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}>
                          {store.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <Badge variant="outline" className="border-purple-400/50 text-purple-400">
                          {store.membership_plan}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{store.description || 'Sin descripción'}</p>
                      <p className="text-xs text-muted-foreground mt-1">Creada: {formatDate(store.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{store.is_active ? 'Activa' : 'Inactiva'}</span>
                        <Switch
                          checked={store.is_active}
                          onCheckedChange={() => handleToggleStore(store)}
                          data-testid={`toggle-store-${store.id}`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Create Store Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="glass-panel border-white/10">
          <DialogHeader>
            <DialogTitle className="font-outfit text-foreground">Crear Nueva Tienda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-foreground">Nombre de la Tienda</Label>
              <Input
                value={newStoreData.name}
                onChange={(e) => setNewStoreData({ ...newStoreData, name: e.target.value })}
                placeholder="Mi Tienda"
                className="bg-white/5 border-white/10 text-foreground"
                data-testid="store-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Descripción</Label>
              <Input
                value={newStoreData.description}
                onChange={(e) => setNewStoreData({ ...newStoreData, description: e.target.value })}
                placeholder="Descripción opcional"
                className="bg-white/5 border-white/10 text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Email del Administrador</Label>
              <Input
                type="email"
                value={newStoreData.owner_email}
                onChange={(e) => setNewStoreData({ ...newStoreData, owner_email: e.target.value })}
                placeholder="admin@ejemplo.com"
                className="bg-white/5 border-white/10 text-foreground"
                data-testid="store-owner-email-input"
              />
              <p className="text-xs text-muted-foreground">Este usuario debe existir previamente en el sistema</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-white/10 text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleCreateStore} disabled={creating} className="bg-indigo-600 hover:bg-indigo-500" data-testid="confirm-create-store-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear Tienda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Admin Panel Router
export default function AdminPanel() {
  return (
    <Routes>
      <Route path="/*" element={<AdminDashboard />} />
    </Routes>
  );
}
